import Button from 'components/Button';
import { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CheckedBlue from 'images/icons/CheckedBlue.svg?react';
import ChevronDown from 'images/icons/ChevronDown.svg?react';
import ChevronUp from 'images/icons/ChevronUp.svg?react';
import Filter from 'images/icons/Filter.svg?react';
import UncheckedBlue from 'images/icons/UncheckedBlue.svg?react';
import PremiumIcon from 'components/PremiumIcon';
import useOutsideClick from 'utils/onOutsideClick';

import { useHistory } from 'react-router-dom';
import {
    SButtonWrapper,
    SFilterButtonLabel,
    SFilterColumn,
    SFilterMenu,
    SFilterOption,
    SPremiumDiv,
    SWrapper,
} from './style';

type Option = {
    component: ReactNode;
    value: string;
};

interface SelectFiltersProps {
    options: Option[];
    selectedOptionsValues: string[];
    setSelectedOptionsValues: Dispatch<SetStateAction<string[]>>;
    disabled?: boolean;
    isPremium: boolean;
    isPremiumFeature?: boolean;
}

const SelectFilters: FC<SelectFiltersProps> = ({
    disabled,
    options,
    selectedOptionsValues,
    setSelectedOptionsValues,
    isPremium,
    isPremiumFeature = false,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const ref = useOutsideClick(() => setIsOpen(false));
    const { t } = useTranslation();
    const history = useHistory();

    const onOptionClick = (option: Option) => {
        if (selectedOptionsValues.includes(option.value)) {
            setSelectedOptionsValues((value) => value.filter((optionValue) => optionValue !== option.value));
        } else {
            setSelectedOptionsValues((value) => [...value, option.value]);
        }
    };

    const firstColumnOptions = options.slice(0, 5);
    const secondColumnOptions = options.slice(5);

    const handleFilterClick = () => {
        if (isPremium) {
            setIsOpen((value) => !value);
        }
    };

    const handleClick = () => {
        if (!isPremium) {
            history.push('/ask-subscription');
        }
    };

    const FilterButton = (
        <div onClick={handleClick} onKeyDown={handleClick} role="button" style={{ position: 'relative' }} tabIndex={0}>
            {isPremiumFeature && (
                <SPremiumDiv>
                    <PremiumIcon />
                </SPremiumDiv>
            )}
            <Button disabled={disabled} onClick={handleFilterClick} variant="outlined" secondary>
                <SFilterButtonLabel>
                    <Filter />
                    {`${t('common.filter', { count: selectedOptionsValues.length })} (${selectedOptionsValues.length})`}
                </SFilterButtonLabel>
                {isOpen ? <ChevronUp /> : <ChevronDown />}
            </Button>
        </div>
    );

    return (
        <SWrapper ref={ref}>
            {FilterButton}
            {isOpen && (
                <SFilterMenu isOpen={isOpen}>
                    <SFilterColumn>
                        {firstColumnOptions.map((option) => (
                            <SFilterOption key={option.value} onClick={() => onOptionClick(option)}>
                                {selectedOptionsValues.includes(option.value) ? <CheckedBlue /> : <UncheckedBlue />}
                                {option.component}
                            </SFilterOption>
                        ))}
                    </SFilterColumn>
                    <SFilterColumn>
                        {secondColumnOptions.map((option) => (
                            <SFilterOption key={option.value} onClick={() => onOptionClick(option)}>
                                {selectedOptionsValues.includes(option.value) ? <CheckedBlue /> : <UncheckedBlue />}
                                {option.component}
                            </SFilterOption>
                        ))}
                    </SFilterColumn>
                    <SButtonWrapper>
                        <Button onClick={() => setSelectedOptionsValues([])} variant="text">
                            {t('common.reset')}
                        </Button>
                    </SButtonWrapper>
                </SFilterMenu>
            )}
        </SWrapper>
    );
};

export default SelectFilters;
